import { createReducer, on } from '@ngrx/store';
import * as WarehouseActions from './warehouse.actions';
import { WarehouseDto } from '../../../../api/gse/models/warehouse-dto';
import { WarehouseLookupDto } from '../../../../api/gse/models/warehouse-lookup-dto';
import { WarehouseDashboardSummaryDto } from '../../../../api/gse/models/warehouse-dashboard-summary-dto';
import { ZoneTypeDistributionDto } from '../../../../api/gse/models/zone-type-distribution-dto';
import { WarehouseZoneCountDto } from '../../../../api/gse/models/warehouse-zone-count-dto';

export interface WarehouseState {
    warehouses: WarehouseDto[];
    /** Subset from GET /warehouses?status=ACTIVE — does not replace {@link warehouses}. */
    activeWarehouses: WarehouseDto[];
    activeWarehousesLoading: boolean;
    selectedWarehouse: WarehouseDto | null;
    lookups: WarehouseLookupDto | null;
    dashboardSummary: WarehouseDashboardSummaryDto | null;
    zoneTypeDistribution: ZoneTypeDistributionDto[];
  warehousesByZoneCount: WarehouseZoneCountDto[];
    listLoading: boolean;
    detailLoading: boolean;
    actionLoading: boolean;
    isLoading: boolean;
    error: string | null;
}

export const initialState: WarehouseState = {
    warehouses: [],
    activeWarehouses: [],
    activeWarehousesLoading: false,
    selectedWarehouse: null,
    lookups: null,
    dashboardSummary: null,
    zoneTypeDistribution: [],
    warehousesByZoneCount: [],
    listLoading: false,
    detailLoading: false,
    actionLoading: false,
    isLoading: false,
    error: null,
};

export const warehouseReducer = createReducer(
    initialState,

    // Load Warehouses
    on(WarehouseActions.loadWarehouses, (state) => ({
        ...state,
        listLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadWarehousesSuccess, (state, { response }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        warehouses: response.data || [],
    })),
    on(WarehouseActions.loadWarehousesFailure, (state, { error }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        error,
    })),

    on(WarehouseActions.loadActiveWarehouses, (state) => ({
        ...state,
        activeWarehousesLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadActiveWarehousesSuccess, (state, { response }) => ({
        ...state,
        activeWarehousesLoading: false,
        activeWarehouses: response.data || [],
    })),
    on(WarehouseActions.loadActiveWarehousesFailure, (state, { error }) => ({
        ...state,
        activeWarehousesLoading: false,
        error,
    })),

    // Load Warehouse Details
    on(WarehouseActions.loadWarehouse, (state) => ({
        ...state,
        detailLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadWarehouseSuccess, (state, { response }) => ({
        ...state,
        detailLoading: false,
        isLoading: false,
        selectedWarehouse: (response.data && response.data.length > 0) ? response.data[0] : null,
    })),
    on(WarehouseActions.loadWarehouseFailure, (state, { error }) => ({
        ...state,
        detailLoading: false,
        isLoading: false,
        error,
    })),

    // Create Warehouse
    on(WarehouseActions.createWarehouse, (state) => ({
        ...state,
        actionLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.createWarehouseSuccess, (state) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
    })),
    on(WarehouseActions.createWarehouseFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
        error,
    })),

    // Update Warehouse
    on(WarehouseActions.updateWarehouse, (state) => ({
        ...state,
        actionLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.updateWarehouseSuccess, (state, { response }) => {
        // Update list if present
        const updated = (response.data && response.data.length > 0) ? response.data[0] : null;
        const updatedWarehouses = state.warehouses.map(w =>
            (updated && w.id === updated.id) ? updated : w
        );
        return {
            ...state,
            actionLoading: false,
            isLoading: false,
            warehouses: updatedWarehouses,
            selectedWarehouse: (state.selectedWarehouse && updated && state.selectedWarehouse.id === updated.id) ? updated : state.selectedWarehouse
        };
    }),
    on(WarehouseActions.updateWarehouseFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
        error,
    })),

    // Zone Actions (Create/Update/Delete) - Set action loading state
    on(WarehouseActions.createZone, (state) => ({
        ...state,
        actionLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.updateZone, (state) => ({
        ...state,
        actionLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.deleteZone, (state) => ({
        ...state,
        actionLoading: true,
        isLoading: true,
        error: null,
    })),

    // Zone Success
    on(WarehouseActions.createZoneSuccess, (state) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
    })),
    on(WarehouseActions.updateZoneSuccess, (state) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
    })),
    on(WarehouseActions.deleteZoneSuccess, (state) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
    })),

    // Zone Failures
    on(WarehouseActions.createZoneFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
        error,
    })),
    on(WarehouseActions.updateZoneFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
        error,
    })),
    on(WarehouseActions.deleteZoneFailure, (state, { error }) => ({
        ...state,
        actionLoading: false,
        isLoading: false,
        error,
    })),

    // Lookups
    on(WarehouseActions.loadLookups, (state) => ({
        ...state,
        detailLoading: true, // Lookups are usually for forms/details
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadLookupsSuccess, (state, { response }) => ({
        ...state,
        detailLoading: false,
        isLoading: false,
        lookups: response.data || null,
    })),
    on(WarehouseActions.loadLookupsFailure, (state, { error }) => ({
        ...state,
        detailLoading: false,
        isLoading: false,
        error,
    })),

    // Dashboard Summary
    on(WarehouseActions.loadDashboardSummary, (state) => ({
        ...state,
        listLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadDashboardSummarySuccess, (state, { response }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        dashboardSummary: response.data || null,
    })),
    on(WarehouseActions.loadDashboardSummaryFailure, (state, { error }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        error,
    })),

    // Zone Type Distribution
    on(WarehouseActions.loadZoneTypeDistribution, (state) => ({
        ...state,
        listLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadZoneTypeDistributionSuccess, (state, { response }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        zoneTypeDistribution: response.data || [],
    })),
    on(WarehouseActions.loadZoneTypeDistributionFailure, (state, { error }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        error,
    })),

    // Warehouses By Zone Count
    on(WarehouseActions.loadWarehousesByZoneCount, (state) => ({
        ...state,
        listLoading: true,
        isLoading: true,
        error: null,
    })),
    on(WarehouseActions.loadWarehousesByZoneCountSuccess, (state, { response }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        warehousesByZoneCount: response.data || [],
    })),
    on(WarehouseActions.loadWarehousesByZoneCountFailure, (state, { error }) => ({
        ...state,
        listLoading: false,
        isLoading: false,
        error,
    }))
);
